import { TextareaAutosize, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import ScrollToTop from "../../ScrollToTop/ScrollToTop";
import { FiPlus, FiMinus, FiX, FiShoppingBag } from "../../../icons";
import "../../../Styles/Cart.css";

const proizvodiLabel = (n: number): string => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  const word = mod10 === 1 && mod100 !== 11 ? "proizvod" : "proizvoda";
  return `${n} ${word}`;
};

interface ISingleCartProductProps {
  productID: number;
  productName: string;
  productMaterial: string;
  productPrice: number;
  productImage: string;
  productAmount: number;
  handleNewTotalOrderPrice: () => void;
  handleProductRemoved: () => void;
}

const SingleCartProduct = ({
  productID,
  productName,
  productMaterial,
  productPrice,
  productImage,
  productAmount,
  handleNewTotalOrderPrice,
  handleProductRemoved,
}: ISingleCartProductProps): JSX.Element => {
  const [amount, setAmount] = useState(productAmount);
  const [totalProductPrice, setTotalProductPrice] = useState(
    productPrice * productAmount
  );

  const openProduct = () => {
    window.open(
      `https://jewelryshopdoro.com/products/${productID}`,
      "_blank"
    );
  };

  const handleRemoveFromCart = () => {
    const cartProductsString = sessionStorage.getItem("products");
    if (cartProductsString) {
      let cartProducts = JSON.parse(cartProductsString);
      if (cartProducts.length > 1) {
        const indexToRemove = cartProducts.findIndex(
          (cp: any) => cp.id === productID
        );
        if (indexToRemove !== -1) {
          cartProducts.splice(indexToRemove, 1);
        }
        sessionStorage.setItem("products", JSON.stringify(cartProducts));
        setTotalProductPrice(productPrice * amount);
      } else {
        sessionStorage.removeItem("products");
      }
      handleProductRemoved();
    }
  };

  const handleAmountChangeInStorage = (newAmount: number) => {
    const cartProductsString = sessionStorage.getItem("products");
    if (cartProductsString) {
      let cartProducts = JSON.parse(cartProductsString);
      for (let cp of cartProducts) {
        if (cp.id === productID) {
          cp.amount = newAmount;
          break;
        }
      }
      sessionStorage.setItem("products", JSON.stringify(cartProducts));
      handleNewTotalOrderPrice();
    }
  };

  const handlePlus = () => {
    const newAmount = amount + 1;
    setAmount(newAmount);
    setTotalProductPrice(productPrice * newAmount);
    handleAmountChangeInStorage(newAmount);
  };

  const handleMinus = () => {
    if (amount > 1) {
      const newAmount = amount - 1;
      setAmount(newAmount);
      setTotalProductPrice(productPrice * newAmount);
      handleAmountChangeInStorage(newAmount);
    }
  };

  return (
    <div className="cart-item">
      <div className="cart-item-product">
        <img
          className="cart-item-image"
          src={`https://jewelryshopdoro.com/uploads/${productImage}`}
          alt={productName}
          onClick={openProduct}
        />
        <span className="cart-item-name" onClick={openProduct}>
          {productName} - {productMaterial}
        </span>
      </div>

      <div className="cart-item-cell cart-item-unit">
        <span className="cart-cell-label">Cena</span>
        <span className="cart-cell-value">{`${productPrice} rsd`}</span>
      </div>

      <div className="cart-item-cell cart-item-qty">
        <span className="cart-cell-label">Količina</span>
        <div className="cart-stepper">
          <button
            type="button"
            className="cart-stepper-btn"
            onClick={handleMinus}
            disabled={amount <= 1}
            aria-label="Smanji količinu"
          >
            <FiMinus />
          </button>
          <span className="cart-stepper-value">{amount}</span>
          <button
            type="button"
            className="cart-stepper-btn"
            onClick={handlePlus}
            aria-label="Povećaj količinu"
          >
            <FiPlus />
          </button>
        </div>
      </div>

      <div className="cart-item-cell cart-item-total">
        <span className="cart-cell-label">Ukupno</span>
        <span className="cart-cell-value cart-item-total-value">
          {`${totalProductPrice.toFixed(2)} rsd`}
        </span>
      </div>

      <div className="cart-item-remove">
        <Tooltip title="Ukloni iz korpe" placement="bottom">
          <button
            type="button"
            className="cart-remove-btn"
            onClick={handleRemoveFromCart}
            aria-label="Ukloni iz korpe"
          >
            <FiX />
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

const Cart = () => {
  const [totalOrderPrice, setTotalOrderPrice] = useState(0.0);
  const [cartProducts, setCartProducts] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    // Delete all order data from storage if just entered the cart
    sessionStorage.removeItem("customerNote");
    sessionStorage.removeItem("orderInformation");

    // Load the products (empty cart renders the empty state, no redirect)
    const cartProductsString = sessionStorage.getItem("products");
    if (cartProductsString) {
      const parsedProducts = JSON.parse(cartProductsString);
      setCartProducts(parsedProducts);
    }

    // Calculate initial total order price
    handleNewTotalOrderPrice();
    setHasLoaded(true);
  }, [navigate]);

  const handleCustomerNote = (noteText: string) => {
    sessionStorage.setItem("customerNote", noteText);
  };

  const handleProductRemoved = () => {
    const cartProductsString = sessionStorage.getItem("products");
    if (cartProductsString) {
      const parsedProducts = JSON.parse(cartProductsString);
      setCartProducts(parsedProducts);
      handleNewTotalOrderPrice();
    } else {
      setCartProducts([]);
    }
  };

  const handleNewTotalOrderPrice = () => {
    const newCartProductsString = sessionStorage.getItem("products");
    if (newCartProductsString) {
      const cartProducts = JSON.parse(newCartProductsString);
      let newTotalOrderPrice = 0.0;
      for (let singleCartProduct of cartProducts) {
        newTotalOrderPrice +=
          singleCartProduct.price * singleCartProduct.amount;
      }
      setTotalOrderPrice(newTotalOrderPrice);
    } else {
      setTotalOrderPrice(0);
    }
  };

  if (!hasLoaded) {
    return (
      <div className="cart-page">
        <ScrollToTop />
      </div>
    );
  }

  if (cartProducts.length === 0) {
    return (
      <div className="cart-page">
        <ScrollToTop />
        <div className="cart-empty">
          <FiShoppingBag className="cart-empty-icon" />
          <h1 className="cart-empty-title">Vaša korpa je prazna</h1>
          <button
            type="button"
            className="cart-empty-btn"
            onClick={() => navigate("/products")}
          >
            Nazad na radnju
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <ScrollToTop />
      <p className="cart-breadcrumb">Početna → Korpa</p>
      <h1 className="cart-title">Vaša korpa</h1>

      <div className="cart-layout">
        <div className="cart-main">
          <div className="cart-list">
            <div className="cart-list-header">
              <span>Proizvod</span>
              <span>Cena</span>
              <span>Količina</span>
              <span>Ukupno</span>
              <span></span>
            </div>
            {cartProducts.map((singleCartProduct: any) => (
              <SingleCartProduct
                key={singleCartProduct.id}
                productID={singleCartProduct.id}
                productName={singleCartProduct.product_name}
                productMaterial={singleCartProduct.material}
                productPrice={singleCartProduct.price}
                productImage={singleCartProduct.main_image}
                productAmount={singleCartProduct.amount}
                handleNewTotalOrderPrice={handleNewTotalOrderPrice}
                handleProductRemoved={handleProductRemoved}
              />
            ))}
          </div>

          <TextareaAutosize
            className="cart-note"
            minRows={4}
            onChange={(event) => handleCustomerNote(event.target.value)}
            placeholder="Napomena"
          />
        </div>

        <aside className="cart-summary">
          <span className="cart-summary-count">{proizvodiLabel(cartProducts.length)}</span>
          <span className="cart-summary-total">{`Ukupno: ${totalOrderPrice.toFixed(
            2
          )} rsd`}</span>
          <button
            type="button"
            className="cart-checkout-btn"
            onClick={() => navigate("/order-info")}
          >
            Nastavite sa plaćanjem
          </button>
          <button
            type="button"
            className="cart-continue-btn"
            onClick={() => navigate("/products")}
          >
            {"‹ Nastavite kupovinu"}
          </button>
        </aside>
      </div>
    </div>
  );
};

export default Cart;
